export interface Feature {
  slug: string;
  name: string;
  tagline: string;
  description: string;
  icon: string;
  benefits: { title: string; description: string }[];
  howItWorks: string[];
  relatedSlugs: string[];
}

export const FEATURES: Feature[] = [
  {
    slug: 'custom-application-forms',
    name: 'Custom Application Forms',
    tagline: 'Build branded application forms in minutes with drag-and-drop simplicity',
    description:
      'Create professional job application forms without writing a single line of code. Drag and drop text inputs, email fields, phone numbers, file uploads, dropdowns, and checkboxes to build the exact form your job posting needs.',
    icon: '📝',
    benefits: [
      {
        title: 'No coding required',
        description:
          'Our visual form builder lets you drag and drop fields like text, email, phone, file upload, dropdown, and checkbox to create forms in minutes.',
      },
      {
        title: 'Mobile-optimized by default',
        description:
          'Every form looks great on phones, tablets, and desktops so you never lose an applicant due to a clunky mobile experience.',
      },
      {
        title: 'Embed anywhere',
        description:
          'Share your form via a direct link, embed it on your website, or paste it as the apply URL on Indeed and LinkedIn.',
      },
    ],
    howItWorks: [
      'Open the form builder and choose from pre-built templates or start from scratch.',
      'Drag and drop fields such as name, email, phone, resume upload, cover letter, and custom questions.',
      'Publish your form and share the link on job boards, social media, or your careers page.',
      'Submissions flow into your applicant tracking dashboard automatically.',
    ],
    relatedSlugs: ['applicant-tracking', 'resume-video-uploads', 'multi-form-support'],
  },
  {
    slug: 'applicant-tracking',
    name: 'Applicant Tracking',
    tagline: 'Track every candidate from application to hire in one clean dashboard',
    description:
      'Never lose track of a candidate again. Our built-in applicant tracking system lets you move candidates through customizable stages, from new application to contacted, interviewed, and hired, all in a single view.',
    icon: '📊',
    benefits: [
      {
        title: 'Visual pipeline',
        description:
          'See every applicant organized by status: new, contacted, interviewed, and hired. Drag candidates between stages with one click.',
      },
      {
        title: 'Centralized candidate profiles',
        description:
          'View resumes, contact info, notes, and interview history in one place for every applicant.',
      },
      {
        title: 'Never miss a follow-up',
        description:
          'Track when you last contacted each candidate and set reminders so no one falls through the cracks.',
      },
    ],
    howItWorks: [
      'Applicants submit your form and automatically appear in the tracking dashboard.',
      'Review submissions and move candidates to "Contacted" when you reach out.',
      'Schedule interviews directly from the dashboard using Google Calendar integration.',
      'Mark candidates as "Hired" to complete the pipeline and track your hiring metrics.',
    ],
    relatedSlugs: ['custom-application-forms', 'submission-analytics', 'team-collaboration'],
  },
  {
    slug: 'resume-video-uploads',
    name: 'Resume & Video Uploads',
    tagline: 'Accept resumes, documents, and video introductions from every applicant',
    description:
      'Let candidates upload PDF resumes, Word documents, and even short video introductions directly through your application form. All files are stored securely on Cloudflare R2 and accessible from your dashboard.',
    icon: '🎥',
    benefits: [
      {
        title: 'Accept multiple file types',
        description:
          'Support PDF, DOCX, and video files so candidates can submit resumes and video introductions in the format they prefer.',
      },
      {
        title: 'Secure cloud storage',
        description:
          'All uploads are stored on Cloudflare R2 with encryption at rest. Access files anytime from your dashboard.',
      },
      {
        title: 'Video introductions',
        description:
          'Get a better sense of candidates before the interview by letting them record and upload a short video introduction.',
      },
    ],
    howItWorks: [
      'Add a file upload field to your application form using the drag-and-drop builder.',
      'Choose accepted file types: PDF, Word, or video.',
      'Candidates upload their files when submitting the application.',
      'View and download all attachments from the applicant profile in your dashboard.',
    ],
    relatedSlugs: ['custom-application-forms', 'applicant-tracking', 'custom-branding'],
  },
  {
    slug: 'google-calendar-integration',
    name: 'Google Calendar Integration',
    tagline: 'Schedule interviews on your Google Calendar and send invites automatically',
    description:
      'Connect your Google Calendar and let iHireFast handle interview scheduling. When you schedule an interview, calendar invites are sent to both you and the candidate automatically, eliminating the back-and-forth.',
    icon: '📅',
    benefits: [
      {
        title: 'Automatic calendar invites',
        description:
          'When an interview is scheduled, both you and the candidate receive a Google Calendar invite with all the details.',
      },
      {
        title: 'Avoid double-bookings',
        description:
          'iHireFast reads your calendar availability in real time so candidates can only book open time slots.',
      },
      {
        title: 'One-click rescheduling',
        description:
          'Need to move an interview? Reschedule from the dashboard and updated invites are sent automatically.',
      },
    ],
    howItWorks: [
      'Connect your Google account in the iHireFast settings page.',
      'Set your available hours and interview duration preferences.',
      'Candidates self-schedule from your public booking page or you assign a time manually.',
      'Both parties receive a Google Calendar event with video call link and details.',
    ],
    relatedSlugs: ['public-booking-pages', 'email-campaigns', 'real-time-notifications'],
  },
  {
    slug: 'email-campaigns',
    name: 'Email Campaigns',
    tagline: 'Email individual candidates or blast all applicants at once',
    description:
      'Communicate with candidates directly from iHireFast. Send personalized emails to individual applicants or broadcast updates to your entire candidate pool. Powered by Resend for reliable delivery.',
    icon: '📧',
    benefits: [
      {
        title: 'Individual or bulk emails',
        description:
          'Send a personal note to one candidate or email all applicants at once with a single click.',
      },
      {
        title: 'Professional templates',
        description:
          'Use branded email templates that match your company identity for a polished candidate experience.',
      },
      {
        title: 'Delivery tracking',
        description:
          'Know when emails are delivered so you can follow up at the right time.',
      },
    ],
    howItWorks: [
      'Open any applicant profile and click "Send Email" to compose a message.',
      'Or select multiple applicants and choose "Email All" for a bulk campaign.',
      'Write your message or pick a template, then hit send.',
      'Emails are delivered via Resend with your custom branding and reply-to address.',
    ],
    relatedSlugs: ['applicant-tracking', 'custom-branding', 'real-time-notifications'],
  },
  {
    slug: 'custom-branding',
    name: 'Custom Branding',
    tagline: 'Your logo, colors, and company name on every form and email',
    description:
      'Make every touchpoint look like your brand. Upload your logo, set your brand colors, and customize your company name so that application forms, emails, and booking pages all reflect your identity.',
    icon: '🎨',
    benefits: [
      {
        title: 'Brand-consistent forms',
        description:
          'Your application forms display your logo and brand colors so candidates know exactly who they are applying to.',
      },
      {
        title: 'Branded emails',
        description:
          'Every automated and manual email includes your logo, company name, and colors for a professional look.',
      },
      {
        title: 'Custom booking pages',
        description:
          'Your public scheduling page carries your branding so the candidate experience feels seamless from start to finish.',
      },
    ],
    howItWorks: [
      'Go to Settings and upload your company logo.',
      'Set your primary brand color using the color picker.',
      'Enter your company name and tagline.',
      'All forms, emails, and booking pages automatically update with your branding.',
    ],
    relatedSlugs: ['custom-application-forms', 'email-campaigns', 'public-booking-pages'],
  },
  {
    slug: 'public-booking-pages',
    name: 'Public Booking Pages',
    tagline: 'Let candidates self-schedule interviews from your available time slots',
    description:
      'Give candidates a link to your public booking page where they can see your available time slots and schedule their own interview. No more back-and-forth emails trying to find a time that works.',
    icon: '🗓️',
    benefits: [
      {
        title: 'Self-service scheduling',
        description:
          'Candidates pick from your open time slots and book instantly, no email chains required.',
      },
      {
        title: 'Real-time availability',
        description:
          'Your booking page syncs with Google Calendar so only genuinely available slots are shown.',
      },
      {
        title: 'Automatic confirmations',
        description:
          'When a candidate books, both parties receive a confirmation email and calendar invite immediately.',
      },
    ],
    howItWorks: [
      'Connect your Google Calendar and set your available hours.',
      'Share your unique booking page link with candidates via email or in your auto-reply.',
      'Candidates view your availability and select a time slot.',
      'Both you and the candidate receive a calendar invite and confirmation email.',
    ],
    relatedSlugs: ['google-calendar-integration', 'email-campaigns', 'custom-branding'],
  },
  {
    slug: 'multi-form-support',
    name: 'Multi-Form Support',
    tagline: 'Create unlimited forms for different job positions',
    description:
      'Hiring for multiple roles? Create a separate application form for each position with its own fields, branding, and tracking. Manage all your forms from a single dashboard.',
    icon: '📋',
    benefits: [
      {
        title: 'One form per role',
        description:
          'Create dedicated forms for each open position so you collect exactly the right information for every job.',
      },
      {
        title: 'Centralized management',
        description:
          'View and manage all your forms from one dashboard. Switch between roles without losing context.',
      },
      {
        title: 'Independent tracking',
        description:
          'Each form has its own applicant pipeline so candidates for different roles never get mixed up.',
      },
    ],
    howItWorks: [
      'Click "New Form" from your dashboard to start building.',
      'Customize fields, branding, and settings for the specific position.',
      'Publish and share the unique link for that role.',
      'Track applicants for each form independently in your dashboard.',
    ],
    relatedSlugs: ['custom-application-forms', 'applicant-tracking', 'submission-analytics'],
  },
  {
    slug: 'indeed-linkedin-ready',
    name: 'Indeed & LinkedIn Ready',
    tagline: 'Paste your form link as the apply URL on any job board',
    description:
      'iHireFast forms work seamlessly with Indeed, LinkedIn, ZipRecruiter, and every other job board that supports external apply URLs. Just paste your form link and start collecting applications.',
    icon: '🔗',
    benefits: [
      {
        title: 'Works with every job board',
        description:
          'Use your iHireFast form link as the external apply URL on Indeed, LinkedIn, ZipRecruiter, Craigslist, and more.',
      },
      {
        title: 'No applicant data lost',
        description:
          'Unlike built-in job board forms, iHireFast captures every submission in your own dashboard with full contact info.',
      },
      {
        title: 'Source tracking',
        description:
          'See which job board each applicant came from so you know where to invest your recruiting budget.',
      },
    ],
    howItWorks: [
      'Create and publish your application form in iHireFast.',
      'Copy the form URL from your dashboard.',
      'Paste it as the "Apply" or "External URL" when posting a job on Indeed, LinkedIn, or any board.',
      'Applications flow directly into your iHireFast dashboard.',
    ],
    relatedSlugs: ['custom-application-forms', 'submission-analytics', 'real-time-notifications'],
  },
  {
    slug: 'real-time-notifications',
    name: 'Real-Time Notifications',
    tagline: 'Get notified instantly when someone applies',
    description:
      'Never miss a new application. iHireFast sends you an instant notification every time a candidate submits your form so you can respond fast and keep top talent engaged.',
    icon: '🔔',
    benefits: [
      {
        title: 'Instant alerts',
        description:
          'Receive a notification the moment a new application comes in so you can respond within minutes, not hours.',
      },
      {
        title: 'Faster response times',
        description:
          'Candidates who get a quick response are far more likely to accept your interview invitation.',
      },
      {
        title: 'Stay on top of volume',
        description:
          'Even when you are receiving dozens of applications a day, notifications keep you current.',
      },
    ],
    howItWorks: [
      'Enable notifications in your iHireFast settings.',
      'Choose your preferred notification channel: email or in-app.',
      'Receive an alert every time a new applicant submits your form.',
      'Click the notification to go directly to the applicant profile and take action.',
    ],
    relatedSlugs: ['applicant-tracking', 'email-campaigns', 'submission-analytics'],
  },
  {
    slug: 'submission-analytics',
    name: 'Submission Analytics',
    tagline: 'Track form views, submission rates, and applicant sources',
    description:
      'Understand how your job posts are performing. iHireFast analytics show you form views, submission rates, and where your applicants are coming from so you can optimize your recruiting strategy.',
    icon: '📈',
    benefits: [
      {
        title: 'Form performance metrics',
        description:
          'See how many people view your form versus how many actually submit, so you can optimize conversion.',
      },
      {
        title: 'Source attribution',
        description:
          'Know whether applicants found you on Indeed, LinkedIn, your website, or social media.',
      },
      {
        title: 'Hiring funnel insights',
        description:
          'Track how candidates move through your pipeline to identify bottlenecks and speed up your time to hire.',
      },
    ],
    howItWorks: [
      'Open the Analytics tab in your iHireFast dashboard.',
      'View form impressions, submissions, and conversion rates over time.',
      'Break down applicants by source to see which channels deliver the best candidates.',
      'Use insights to adjust your job posting strategy and improve results.',
    ],
    relatedSlugs: ['applicant-tracking', 'indeed-linkedin-ready', 'multi-form-support'],
  },
  {
    slug: 'team-collaboration',
    name: 'Team Collaboration',
    tagline: 'Multiple team members can access the same workspace',
    description:
      'Hiring is a team effort. Invite colleagues to your iHireFast workspace so everyone can review applicants, leave notes, and coordinate on hiring decisions without forwarding emails or sharing spreadsheets.',
    icon: '👥',
    benefits: [
      {
        title: 'Shared workspace',
        description:
          'Invite team members to your workspace so everyone has access to the same applicant data in real time.',
      },
      {
        title: 'Internal notes',
        description:
          'Leave private notes on any applicant profile so your team can share feedback without emailing back and forth.',
      },
      {
        title: 'Coordinated hiring',
        description:
          'Assign interviewers, track who has reviewed each candidate, and make collaborative hiring decisions.',
      },
    ],
    howItWorks: [
      'Go to Settings and invite team members by email.',
      'Team members sign in and see the shared applicant dashboard.',
      'Leave notes, update statuses, and schedule interviews collaboratively.',
      'Everyone stays in sync without spreadsheets, email threads, or Slack messages.',
    ],
    relatedSlugs: ['applicant-tracking', 'google-calendar-integration', 'email-campaigns'],
  },
];

export function getFeatureBySlug(slug: string): Feature | undefined {
  return FEATURES.find((f) => f.slug === slug);
}
